import React, { useContext, useState } from 'react';
import { TreeContext } from '../../context/TreeContext';
import '../../styles/components/ControlColumn.css';

// Helper components
const NumberControl = ({ label, field, min, max, value, handleChange }) => (
  <div className="control-input">
    <label>{label}</label>
    <input
      type="number"
      min={min}
      max={max}
      value={value}
      onChange={handleChange(field)}
    />
  </div>
);

const RangeControl = ({ label, field, min, max, step = 0.1, value, handleChange }) => (
  <div className="control-input range-control">
    <label>{label}: {value}</label>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      onChange={handleChange(field)}
    />
  </div>
);

const Divider = () => <div className="divider"></div>;

const ToggleButton = ({ field, onText, offText, handleToggle, isActive }) => (
  <button
    className={`toggle-button ${isActive ? 'active' : ''}`}
    onClick={handleToggle(field)}
  >
    {isActive ? onText : offText}
  </button>
);

const ControlGroup = ({ label, children, rightContent }) => (
  <div className="control-group">
    {label && (
      <div className="control-group-label">
        {label}
        {rightContent}
      </div>
    )}
    {children}
  </div>
);

const TreeControlsLeft = React.memo(({ handleChange, handleToggle }) => {
  const { state } = useContext(TreeContext);
  
  // Local state for collapsing each section
  const [trunkOpen, setTrunkOpen] = useState(true);
  const [branchesOpen, setBranchesOpen] = useState(true);
  const [foliageShapeOpen, setFoliageShapeOpen] = useState(true);
  
  return (
    <div className="ControlColumn left-column">
      {/* Tree Structure Section */}
      <div className="controlSection">
        <h3 onClick={() => setTrunkOpen(prev => !prev)} className="section-title">
          {trunkOpen ? '▾' : '▸'} Trunk
        </h3>
        {trunkOpen && (
          <div className="vertical-controls">
            <RangeControl 
              label="Tree Height" 
              field="treeHeight" 
              min={10} 
              max={90} 
              step={1}
              value={state.treeHeight}
              handleChange={handleChange}
            />
            <RangeControl 
              label="Tree Thickness" 
              field="treeThickness" 
              min={1} 
              max={6} 
              step={1}
              value={state.treeThickness}
              handleChange={handleChange}
            />
          </div>
        )}
      </div>
      
      <Divider />
      
      {/* Branch System Section */}
      <div className="controlSection">
        <h3 onClick={() => setBranchesOpen(prev => !prev)} className="section-title">
          {branchesOpen ? '▾' : '▸'} Branches
        </h3>
        <div className="control-toggle-row">
          <label>Enable Branches:</label>
          <ToggleButton 
            field="enableBranches" 
            onText="◼ On" 
            offText="◻ Off"
            handleToggle={handleToggle}
            isActive={state.enableBranches}
          />
        </div>
        {branchesOpen && state.enableBranches && (
          <div className="vertical-controls">
            <RangeControl 
              label="Start Height" 
              field="branchStartHeight" 
              min={0.1} 
              max={0.9} 
              step={0.05}
              value={state.branchStartHeight}
              handleChange={handleChange}
            />
            <div className="range-slider-group">
              <label>Branch Length Range: {state.branchLengthMin} - {state.branchLengthMax}</label>
              <div className="dual-slider">
                <RangeControl 
                  label="Min" 
                  field="branchLengthMin" 
                  min={6} 
                  max={Math.min(60, state.branchLengthMax)} 
                  step={1}
                  value={state.branchLengthMin}
                  handleChange={handleChange}
                />
                <RangeControl 
                  label="Max" 
                  field="branchLengthMax" 
                  min={Math.max(6, state.branchLengthMin)} 
                  max={60} 
                  step={1}
                  value={state.branchLengthMax}
                  handleChange={handleChange}
                />
              </div>
            </div>
            <RangeControl 
              label="Branch Density" 
              field="branchDensity" 
              min={0.1} 
              max={1.0} 
              step={0.05}
              value={state.branchDensity}
              handleChange={handleChange}
            />
            <NumberControl 
              label="Recursion Levels" 
              field="branchRecursion" 
              min={1} 
              max={3} 
              value={state.branchRecursion}
              handleChange={handleChange}
            />
          </div>
        )}
      </div>
      
      <Divider />
      
      {/* Foliage Shape Section */}
      <div className="controlSection">
        <h3 onClick={() => setFoliageShapeOpen(prev => !prev)} className="section-title">
          {foliageShapeOpen ? '▾' : '▸'} Foliage Shape
        </h3>
        <div className="control-toggle-row">
          <label>Show Foliage:</label>
          <ToggleButton 
            field="showFoliage" 
            onText="◼ On" 
            offText="◻ Off"
            handleToggle={handleToggle}
            isActive={state.showFoliage}
          />
        </div>
        {foliageShapeOpen && state.showFoliage && (
          <div className="vertical-controls">
            <div className="control-row shape-buttons">
              <button 
                className={`shape-button ${state.foliageShape === 'round' ? 'active' : ''}`}
                onClick={() => handleChange('foliageShape')({ target: { value: 'round' } })}
              >
                Round
              </button>
              <button 
                className={`shape-button ${state.foliageShape === 'conical' ? 'active' : ''}`}
                onClick={() => handleChange('foliageShape')({ target: { value: 'conical' } })}
              >
                Conical
              </button>
              <button 
                className={`shape-button ${state.foliageShape === 'blocky' ? 'active' : ''}`}
                onClick={() => handleChange('foliageShape')({ target: { value: 'blocky' } })}
              >
                Blocky
              </button>
            </div>
            
            <NumberControl 
              label="Foliage Height" 
              field="foliageHeight" 
              min={3} 
              max={30} 
              value={state.foliageHeight}
              handleChange={handleChange}
            />
            
            <NumberControl 
              label="Foliage Width" 
              field="foliageWidth" 
              min={3} 
              max={30} 
              value={state.foliageWidth}
              handleChange={handleChange}
            />
          </div>
        )}
      </div>
    </div>
  );
});

export default TreeControlsLeft; 